import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { useAuth } from '../../contexts/AuthContext';
import { fetchNavigation, saveNavigation } from '../../services/site';

const emptyItem = { label: '', path: '', visible: true, order: 0 };

const NavigationManager = () => {
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [items, setItems] = useState([emptyItem]);

  useEffect(() => {
    const init = async () => {
      try {
        setLoading(true);
        const data = await fetchNavigation();
        const sorted = (data.items || []).sort((a,b) => (a.order ?? 0) - (b.order ?? 0));
        setItems(sorted.length ? sorted : [emptyItem]);
      } catch (e) {
        setError(e.response?.data?.detail || e.message);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  if (!user || user.user_type !== 'fondateur') {
    return <div className="p-6 text-sm text-red-500">Accès réservé au fondateur.</div>;
  }

  const addItem = () => setItems(prev => [...prev, { ...emptyItem, order: prev.length }]);
  const removeItem = (idx) => setItems(prev => prev.filter((_, i) => i !== idx));

  const updateItem = (idx, key, value) => {
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, [key]: value } : it));
  };

  const move = (idx, dir) => {
    const newItems = [...items];
    const target = idx + dir;
    if (target < 0 || target >= newItems.length) return;
    const tmpOrder = newItems[idx].order;
    newItems[idx].order = newItems[target].order;
    newItems[target].order = tmpOrder;
    [newItems[idx], newItems[target]] = [newItems[target], newItems[idx]];
    setItems(newItems);
  };

  const onSave = async () => {
    try {
      setSaving(true); setError(''); setSuccess('');
      // Normalize orders
      const normalized = items.map((it, i) => ({ ...it, order: i }));
      await saveNavigation(normalized, token);
      setSuccess('✅ Navigation enregistrée');
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <Card>
        <CardHeader>
          <CardTitle>Navigation Manager</CardTitle>
          <CardDescription>Créez, réordonnez, masquez ou supprimez vos éléments de menu</CardDescription>
        </CardHeader>
        <CardContent>
          {error && <Alert><AlertDescription className="text-red-600">{error}</AlertDescription></Alert>}
          {success && <Alert><AlertDescription className="text-green-600">{success}</AlertDescription></Alert>}

          <div className="space-y-3">
            {items.map((it, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                <input className="col-span-3 rounded-md border border-border bg-background px-3 py-2" placeholder="Label" value={it.label} onChange={e => updateItem(idx, 'label', e.target.value)} />
                <input className="col-span-4 rounded-md border border-border bg-background px-3 py-2" placeholder="/path" value={it.path} onChange={e => updateItem(idx, 'path', e.target.value)} />
                <label className="col-span-2 text-sm flex items-center gap-2">
                  <input type="checkbox" checked={!!it.visible} onChange={e => updateItem(idx, 'visible', e.target.checked)} /> Visible
                </label>
                <div className="col-span-3 flex gap-2 justify-end">
                  <Button variant="outline" onClick={() => move(idx, -1)}>↑</Button>
                  <Button variant="outline" onClick={() => move(idx, 1)}>↓</Button>
                  <Button variant="outline" onClick={() => removeItem(idx)}>Supprimer</Button>
                </div>
              </div>
            ))}
            <div className="flex justify-between mt-4">
              <Button variant="outline" onClick={addItem}>Ajouter un élément</Button>
              <Button onClick={onSave} disabled={saving}>{saving ? 'Enregistrement…' : 'Sauvegarder la navigation'}</Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default NavigationManager;